/**
 * Flashcard Backend Service - Lưu flashcards vào server thay vì localStorage
 * Sync với user account (Google Auth)
 */

import type { Flashcard, FlashcardDraft, PDFDocument } from '../../types/flashcard'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

class FlashcardBackendService {
  /**
   * Get auth token từ localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('google_auth_token')
  }

  /**
   * Get user email từ localStorage
   */
  private getUserEmail(): string | null {
    const userDataString = localStorage.getItem('google_auth_user')
    if (!userDataString) return null
    
    try {
      const userData = JSON.parse(userDataString)
      return userData.email || null
    } catch {
      return null
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken() && !!this.getUserEmail()
  }

  /**
   * Save flashcards to server
   */
  async saveFlashcards(
    drafts: FlashcardDraft[],
    documentId: string,
    documentName: string
  ): Promise<Flashcard[]> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      throw new Error('User not authenticated')
    }

    try {
      const response = await fetch(`${API_BASE_URL}/flashcards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userEmail,
          documentId,
          documentName,
          flashcards: drafts
        })
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const result = await response.json()
      console.log('✅ Saved', result.flashcards.length, 'flashcards to server')
      
      return result.flashcards
    } catch (error) {
      console.error('Error saving flashcards to server:', error)
      throw new Error('Không thể lưu flashcards lên server')
    }
  }

  /**
   * Get all flashcards for current user
   */
  async getAllFlashcards(): Promise<Flashcard[]> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      return []
    }

    try {
      const response = await fetch(`${API_BASE_URL}/flashcards?userEmail=${encodeURIComponent(userEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const result = await response.json()
      
      // Convert date strings to Date objects
      return result.flashcards.map((f: any) => ({
        ...f,
        createdAt: new Date(f.createdAt),
        updatedAt: new Date(f.updatedAt),
        lastReviewed: f.lastReviewed ? new Date(f.lastReviewed) : undefined,
        nextReview: f.nextReview ? new Date(f.nextReview) : undefined
      }))
    } catch (error) {
      console.error('Error loading flashcards from server:', error)
      return []
    }
  }

  /**
   * Get flashcards by document ID
   */
  async getFlashcardsByDocument(documentId: string): Promise<Flashcard[]> {
    const allFlashcards = await this.getAllFlashcards()
    return allFlashcards.filter(f => f.documentId === documentId)
  }

  /**
   * Save PDF document metadata
   */
  async savePDFDocument(document: Omit<PDFDocument, 'id'>): Promise<PDFDocument> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      throw new Error('User not authenticated')
    }

    try {
      const response = await fetch(`${API_BASE_URL}/pdf-documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userEmail,
          document
        })
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const result = await response.json()
      return result.document
    } catch (error) {
      console.error('Error saving PDF document to server:', error)
      throw new Error('Không thể lưu tài liệu lên server')
    }
  }

  /**
   * Get all PDF documents for current user
   */
  async getAllPDFDocuments(): Promise<PDFDocument[]> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      return []
    }

    try {
      const response = await fetch(`${API_BASE_URL}/pdf-documents?userEmail=${encodeURIComponent(userEmail)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const result = await response.json()
      
      return result.documents.map((d: any) => ({
        ...d,
        uploadedAt: new Date(d.uploadedAt)
      }))
    } catch (error) {
      console.error('Error loading PDF documents from server:', error)
      return []
    }
  }

  /**
   * Delete PDF document and its flashcards
   */
  async deletePDFDocument(documentId: string): Promise<boolean> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      throw new Error('User not authenticated')
    }

    try {
      const response = await fetch(`${API_BASE_URL}/pdf-documents/${documentId}?userEmail=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      return true
    } catch (error) {
      console.error('Error deleting PDF document from server:', error)
      return false
    }
  }

  /**
   * Record review for flashcard (spaced repetition)
   */
  async recordReview(
    flashcardId: string,
    quality: number,
    correct: boolean
  ): Promise<void> {
    const token = this.getAuthToken()
    const userEmail = this.getUserEmail()

    if (!token || !userEmail) {
      throw new Error('User not authenticated')
    }

    try {
      await fetch(`${API_BASE_URL}/flashcards/${flashcardId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userEmail,
          quality,
          correct
        })
      })
    } catch (error) {
      console.error('Error recording review:', error)
    }
  }
}

export const flashcardBackendService = new FlashcardBackendService()

